import { assign } from 'lodash';
import { ChangeDetectionStrategy, Component, type OnInit, ViewChild, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, FormControl } from '@angular/forms';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatDesigns from "@medsurf/flat-designs";
import * as FlatFacades from '@medsurf/flat-facades';
import { ZodSharedValidator } from '@medsurf/flat-validators';
import { MediaControlService } from '@medsurf/flat-services';

@Component({
    selector: 'medsurf-metadata',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './metadata.component.html',
    styleUrls: ['./metadata.component.scss'],
    standalone: false
})
export class MetadataComponent implements OnInit {
  @ViewChild('carouselTitleInputText', {static: false}) carouselTitleInputText: FlatDesigns.TextInputFormControlDesign;
  
  isSubmitted = signal(false);
  #metadata: FlatModels.MetadataEntityModels.Metadata | null;
  metadataForm = new FormGroup(
    {
      carouselTitle: new FormControl(''),
      carouselCopyright: new FormControl(''),
      carouselTextColor: new FormControl(''),
      carouselShowSubjects: new FormControl(false),
      title: new FormControl(''),
      keywords: new FormControl(''),
      description: new FormControl(''),
      supportEmail: new FormControl(''),
      carouselMedia: new FormControl(''),
    },
    {
      validators: ZodSharedValidator.validate(
        FlatModels.MetadataEntityModels.MetadataSchema
        .pick({
          carouselTitle: true,
          carouselCopyright: true,
          carouselTextColor: true,
          carouselShowSubjects: true,
          title: true,
          keywords: true,
          description: true,
          supportEmail: true,
          carouselMedia: true,
        })
      ),
      updateOn: 'change',
    }
  );
  readonly FlatModels = FlatModels;

  constructor(public appSharedFacade: FlatFacades.AppSharedFacade,
              public alertControlFacade: FlatFacades.AlertControlFacade,
              public navigationControlFacade: FlatFacades.NavigationControlFacade,
              public mediaControlService: MediaControlService) {
    // Update Metadata
    this.appSharedFacade.metadata$.pipe(takeUntilDestroyed()).subscribe((metadata) => {
      this.#metadata = structuredClone(metadata);
      if (this.#metadata) {
        this.metadataForm.reset(this.#metadata);
      } else {
        this.navigationControlFacade.requestNavigate(['']);
      }
      this.isSubmitted.set(false);
    });
  }

  ngOnInit(): void {
    // Autofocus
    setTimeout(() => {
      this.carouselTitleInputText?.inputElement?.nativeElement?.focus();
    });
  }
  
  onMediaChange(mediaId: string | null) {
    this.metadataForm.patchValue({carouselMedia: mediaId});
    return this.onSubmit();
  }

  onSubmit() {
    if (this.isSubmitted() || this.metadataForm.invalid || !this.#metadata) {
      return;
    }
    this.isSubmitted.set(true);
    
    // Update Metadata
    const metadata = assign(this.#metadata, this.metadataForm.value);
    this.appSharedFacade.requestUpdateMetadata(metadata).subscribe({
      error: (result) => {
        ZodSharedValidator.handleError(result, this.alertControlFacade, this.metadataForm);
        this.isSubmitted.set(false);
      },
      next: () => {
        this.isSubmitted.set(false);
        this.alertControlFacade.requestAddAlert({
          key: 'main',
          detail: 'notification.saved',
          severity: FlatModels.AlertControlModels.AlertType.enum.success,
          life: FlatModels.AlertControlModels.AlertTime.DEFAULT
        });
      }
    });
  }
}
